import { useMutation, useQuery } from "@tanstack/react-query";
import { useAlert } from "context/alert";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  createHeaderFooterImage,
  deleteHeaderFooterImage,
  fetchHeaderFooterImageList,
} from "services/headerFooterDetail";
import {
  getFranchiseList,
  getZoneList,
} from "../../redux/action/drop_list_action";
import Select from "react-select";
import zoneLocalStorageId from "utils/zone";
import { STALE_TIME } from "utils/constants";

const UploadImages = ({ onClose }) => {
  const { showAlert } = useAlert();
  const dispatch = useDispatch();

  const [headerChecked, setHeaderChecked] = useState(false);
  const [footerChecked, setFooterChecked] = useState(false);
  const [file, setFile] = useState(null);
  const [fileError, setFileError] = useState(false);
  const [fileData, setFileData] = useState("");
  const [selectedZoneId, setSelectedZoneId] = useState(null);
  const [selectedFranchiseId, setSelectedFranchiseId] = useState(null);
  const [zoneError, setZoneError] = useState(false);
  const [checkboxError, setCheckboxError] = useState(false);

  const jwtAuthreducer = useSelector((state) => state.jwtAuthreducer);
  const { zone_list, franchise_list } = useSelector(
    (state) => state.dropListReducer
  );

  const _token =
    jwtAuthreducer?.token_values?.sub &&
    JSON.parse(jwtAuthreducer?.token_values?.sub?.replace(/'/g, '"'));

  const { mutateAsync: addHeaderFooterImageMutate } = useMutation(
    createHeaderFooterImage,
    {
      onSuccess: () => {
        showAlert("Image uploaded successfully!", "success");
        onClose(true);
      },
      onError: () => {
        showAlert("Something went wrong!", "danger");
      },
    }
  );

  const { mutateAsync: deleteHeaderFooterImageMutate } = useMutation(
    deleteHeaderFooterImage,
    {
      onError: () => {
        showAlert("Something went wrong!", "danger");
      },
    }
  );

  const { data } = useQuery(
    [
      "fetch-report-header-footer-list",
      headerChecked,
      footerChecked,
      selectedZoneId,
      selectedFranchiseId,
    ],
    () =>
      fetchHeaderFooterImageList({
        zone_id: selectedZoneId || zoneLocalStorageId(),
        franchise_id: selectedFranchiseId,
        is_active: true,
        created_by: jwtAuthreducer.user.employee_code_id,
        header: headerChecked,
        footer: footerChecked,
      }),
    {
      enabled: !!((headerChecked || footerChecked) && selectedZoneId),
      staleTime: STALE_TIME,
      onSuccess: (res) => {},
      onError: (err) => {
        console.error("Error fetching Header Footer:", err);
      },
    }
  );

  const imageData = data?.data?.data;

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    setFile(selectedFile);
    if (selectedFile) {
      const reader = new FileReader();
      reader.onload = () => {
        const base64String = reader.result;
        const base64Data = base64String.split(",")[1];
        setFileData(base64Data);
      };
      reader.readAsDataURL(selectedFile);
    }
    setFileError(!selectedFile);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const hasCheckboxError = !headerChecked && !footerChecked;
    const hasFileError = !file;
    const hasZoneError = !selectedZoneId;

    setCheckboxError(hasCheckboxError);
    setFileError(hasFileError);
    setZoneError(hasZoneError);

    if (hasCheckboxError || hasFileError || hasZoneError) return;

    const payload = {
      header: headerChecked,
      footer: footerChecked,
      file_name: file.name,
      file_path: file.name,
      file_data: fileData,
      zone_id: selectedZoneId || Number(_token.zone_id),
      franchise_id: selectedFranchiseId || null,
      is_active: true,
      created_by: jwtAuthreducer.user.employee_code_id,
    };

    if (imageData && imageData.length > 0) {
      await Promise.all(imageData.map((image) => {
        if (image?.id) {
          return deleteHeaderFooterImageMutate(image.id);
        }
        return Promise.resolve();
      }));
    }
    addHeaderFooterImageMutate(payload);
  };

  useEffect(() => {
    dispatch(getZoneList());
    dispatch(getFranchiseList());
  }, [dispatch]);

  return (
    <form onSubmit={handleSubmit}>
      <div className="d-flex flex-wrap gap-3 align-items-start mt-3">
        <div className="flex-fill">
          <label className="form-label">
            Select Options <span className="text-danger">*</span>
          </label>
          <div className="d-flex flex-wrap gap-3 mt-2 ps-2">
            <div className="form-check">
              <input
                type="checkbox"
                className="form-check-input"
                id="headerCheckbox"
                checked={headerChecked}
                onChange={(e) => {
                  setHeaderChecked(e.target.checked);
                  if (checkboxError) setCheckboxError(false);
                }}
              />
              <label htmlFor="headerCheckbox" className="form-check-label">
                Header
              </label>
            </div>
            <div className="form-check">
              <input
                type="checkbox"
                className="form-check-input"
                id="footerCheckbox"
                checked={footerChecked}
                onChange={(e) => {
                  setFooterChecked(e.target.checked);
                  if (checkboxError) setCheckboxError(false);
                }}
              />
              <label htmlFor="footerCheckbox" className="form-check-label">
                Footer
              </label>
            </div>
          </div>
          {checkboxError && (
            <div className="text-danger mt-2">
              Please select at least one option (Header or Footer).
            </div>
          )}
        </div>
        <div className="flex-fill">
          <label className="form-label">
            Zone <span className="text-danger">*</span>
          </label>
          <Select
            options={zone_list?.zone_data?.map((item) => ({
              value: item.zone_name,
              label: item.zone_name,
              zone_id: item.zone_id,
            }))}
            isClearable
            placeholder="Select Zone"
            menuPortalTarget={document.body}
            styles={{
              menuPortal: (base) => ({ ...base, zIndex: 9999 }),
            }}
            onChange={(val) => {
              setSelectedZoneId(val?.zone_id || null);
              setZoneError(!val);
            }}
          />
          {zoneError && (
            <div className="text-danger mt-1">Zone selection is required.</div>
          )}
        </div>
        <div className="flex-fill">
          <label className="form-label">Franchise Override</label>
          <Select
            options={franchise_list?.data?.map((item) => ({
              value: item.company_name,
              label: item.company_name,
              franchise_id: item.id,
            }))}
            isClearable
            placeholder="Use Zone Default"
            menuPortalTarget={document.body}
            styles={{
              menuPortal: (base) => ({ ...base, zIndex: 9999 }),
            }}
            onChange={(val) => {
              setSelectedFranchiseId(val?.franchise_id || null);
            }}
          />
          <div className="text-muted mt-1" style={{ fontSize: "12px" }}>
            Leave empty to upload a zone-level default image.
          </div>
        </div>
      </div>
      <div className="row mt-3">
        <div className="col-12">
          <label className="form-label">
            Upload Document <span className="text-danger">*</span>
          </label>
          <input
            type="file"
            className={`form-control ${fileError ? "is-invalid" : ""}`}
            onChange={handleFileChange}
            accept=".pdf,image/*"
          />
          {fileError && (
            <div className="text-danger mt-1">File is required.</div>
          )}
        </div>
      </div>
      <div className="mt-4">
        <button type="submit" className="btn btn-primary">
          Upload
        </button>
      </div>

      {imageData &&
        imageData.map((item, index) => (
          <div key={index} style={{ margin: "10px", textAlign: "center" }}>
            {/* Label */}
            <div
              style={{
                marginBottom: "8px",
                fontWeight: "bold",
                fontSize: "12px",
                color: "#333",
              }}
            >
              {item.header && item.footer && (
                <span style={{ color: "#6f42c1", fontSize: "24px" }}>
                  Already Uploaded Header & Footer
                </span>
              )}
              {item.header && !item.footer && (
                <span style={{ color: "#007bff", fontSize: "24px" }}>
                  Already Uploaded Header
                </span>
              )}
              {!item.header && item.footer && (
                <span style={{ color: "#28a745", fontSize: "24px" }}>
                  Already Uploaded Footer
                </span>
              )}
              {item.franchise_name ? (
                <div style={{ fontSize: "14px", color: "#555" }}>
                  Franchise: {item.franchise_name}
                </div>
              ) : (
                <div style={{ fontSize: "14px", color: "#555" }}>
                  Zone Default
                </div>
              )}
            </div>

            <img
              src={`data:image/jpeg;base64,${item.file_data}`}
              alt="Header Footer Image"
              style={{ width: "400px", height: "300px" }}
            />
          </div>
        ))}
    </form>
  );
};

export default UploadImages;
